import { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import {
  reportFilterCountryCodeSelector,
  reportGcsFilterSelector,
  reportGcsPaymentTypesSelector,
  downloadReportPdf,
} from '../../state';
import { GcsByPaymentTypes } from '../../types';
import { DataGridFlexSales, useDataGridFlexSales, useDataGridSync } from '../DataGridFlexSales';
import handlerFactory, { GcsByPaymentTypeDataGridContext } from './handlerFactory';

const DataGridConfig = () => {
  const { handler } = useDataGridFlexSales<GcsByPaymentTypes, GcsByPaymentTypeDataGridContext>();
  const { t } = useTranslation();
  const country = useSelector(reportFilterCountryCodeSelector);
  const reportGcsFilters = useSelector(reportGcsFilterSelector);

  const { loading, data } = useSelector(reportGcsPaymentTypesSelector);

  const gridContext = useMemo(() => ({ t, country }), [t, country]);

  useDataGridSync({
    handler,
    data,
    context: gridContext,
    title: t('gcs-payment-type.title'),
    loading,
    filters: reportGcsFilters,
  });

  return null;
};

const DataGridExportConfig = () => {
  const { handler } = useDataGridFlexSales<GcsByPaymentTypes, GcsByPaymentTypeDataGridContext>();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    handler.setExportConfig({
      XLSX: { includeTotals: false },
      PDF: {
        includeTotals: false,
        generator: (table, title) => {
          dispatch(
            downloadReportPdf({
              locale: handler.locale,
              t,
              table,
              title,
              includeMissingClosingTable: false,
              parameters: [
                'countryCode',
                'stores',
                'pos',
                'salesTypes',
                'segments',
                'timeBand',
                'hourRange',
                'startAccountingDate',
                'endAccountingDate',
                'startOperationDate',
                'endOperationDate',
                'startBusinessDate',
                'endBusinessDate',
                'groupBy',
              ],
              opStructures: [
                'regionalMgmt',
                'region',
                'management',
                'supervision',
                'agreement',
                'company',
              ],
            })
          );
        },
        isDispatchAction: true,
      },
    });
  }, [handler, dispatch, t]);

  return null;
};

const GcsByPaymentTypeDataGrid = () => (
  <DataGridFlexSales handlerFactory={handlerFactory} hideSummaryRow>
    <DataGridConfig />
    <DataGridExportConfig />
  </DataGridFlexSales>
);

export default GcsByPaymentTypeDataGrid;
